import React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import withTranslations from '../../hocs/withTranslations';
import { ITranslations } from '../../interfaces/ITranslationFields';
import { breakpoint } from '@estee/elc-base-theme';
import { ResponsiveModal } from '@estee/elc-responsive-modal';
import { RemoveIcon } from '@estee/elc-icons';
import { LoadableButton, OutlinedButton } from '@estee/elc-buttons';
import { Dropdown, IOptionItem } from '@estee/elc-dropdown';
import { LayoutPosition } from '@estee/elc-layouts';
import Store, { INVENTORY } from '../../domain/entities/Store/Store';
import ShoppingBag from '../ShoppingBag';
import RadioBox from '../RadioBox';
import CartItem from '../../domain/entities/Cart/CartItem';
import WarningMessage from '../WarningMessage/WarningMessage';
import TranslationUtils from '../../utils/TranslationUtils';

const ShoppingBagWrapper = styled.div`
  .acac-shopping-bag-wrapper {
    margin: 0px;
    border: 0;
    padding: 0;
  }
`;

const StyledRemoveIcon = styled(RemoveIcon)`
  position: absolute;
  right: 16px;
  top: 25px;
  height: 15px;
  width: 15px;
`;

const StyledResponsiveModal = styled(ResponsiveModal)`
  top: 0;
  left: 0;
  width: auto;
  padding: 50px 16px;
  position: relative;
  transform: none;
  min-height: 100%;
  ${breakpoint('desktop')`
    width: 47%;
    transform: translateX(-50%);
    left: 50%;
    padding: 40px;
    min-height: auto;
  `}
`;

const StyledDropdown = styled(Dropdown)`
  ${breakpoint('desktop')`
    width: calc(90% - 40px);
  `}
  ${breakpoint('largeScreen')`
   width: calc(60% - 30px);
  `}
`;

const WrapperStyledDropdown = styled.div`
  margin-top: 10px;
  .elc-floating-label-input-field {
    padding-left: 32px;
    margin-bottom: 10px;
    @media only screen and (max-width: 960px) {
      width: calc(100% - 32px);
    }
  }
  .elc-floating-label-string {
    left: 45px;
    margin-top: -3px;
  }
  .elc-floating-label-string::before{
    background-color: transparent;
  }
`;

const EditCartInfoWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  margin-top: 30px;
  ${breakpoint('desktop')`
    flex-direction: row;
  `}
`;

const EditCartItemsWrapper = styled.div``;

const EditCartControlsWrapper = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column-reverse;
  ${breakpoint('desktop')`
    flex-direction: row;
  `}
`;

const CancelButton = styled(OutlinedButton)`
  margin: 10px 0;
  min-height: 50px;
  width: 100%;
  ${breakpoint('desktop')`
    width: 50%;
    margin: 5px;
  `}
`;

const SubmitButton = styled(LoadableButton)`
  margin: 10px 0;
  min-height: 50px;
  width: 100%;
  ${breakpoint('desktop')`
    width: 50%;
    margin: 5px;
  `}
`;

export enum EDIT_CART_OPTION {
  SHIP_ALL_ITEMS,
  CHANGE_STORE,
  REMOVE_ITEMS,
}

interface IEditCartProps {
  translations?: ITranslations;
  onEdit: (option: EDIT_CART_OPTION, storeId: string) => void;
  onClose: () => void;
  store: Store;
  storesList?: Store[];
  getPrice: (price: number) => string;
  isCourier?: boolean;
  isEditCartBtnLoading?: boolean;
  onSelectDifferentStore?: (store: Store) => void;
  editCartScreenViewEvent?: (cartItems: CartItem[]) => void;
  cartItemsUnavailable: CartItem[];
  isOnlyGWPUnavailable: boolean;
  isSampleInCart: boolean;
  showStoreNameWithAddress?: boolean;
}

@observer
class EditCart extends React.Component<IEditCartProps> {
  @observable selectedOption: EDIT_CART_OPTION = EDIT_CART_OPTION.SHIP_ALL_ITEMS;

  @observable storeId: string = undefined;

  @action
  private onSelectDifferentStore = (name: string, value: string): void => {
    const { onSelectDifferentStore, store } = this.props;
    this.storeId = value;
    if (onSelectDifferentStore) {
      onSelectDifferentStore(store);
    }
  };

  @action
  private onChangeEditOption = (value: EDIT_CART_OPTION): void => {
    this.selectedOption = value;
  };

  @action
  onSubmit = (): void => {
    const { onEdit } = this.props;
    onEdit(this.selectedOption, this.storeId);
  };

  private onCloseKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const { onClose } = this.props;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClose();
    }
  };

  componentDidMount(): void {
    const {
      editCartScreenViewEvent,
      cartItemsUnavailable,
    } = this.props;
    if (editCartScreenViewEvent) {
      editCartScreenViewEvent(cartItemsUnavailable);
    }
  }

  render(): JSX.Element {
    const {
      translations: {
        editBagFirstCopy,
        editBagDescription,
        unavailableItems,
        selectOption,
        selectAllItems,
        selectOtherStore,
        selectStore,
        removeItemsAndContinue,
        removeItemsAndContinueWithGift,
        continue: continueLabel,
        cancel,
        clickCollectTitle,
        sameDayDelivery,
        samplesInfoMessage,
      },
    } = this.props;

    const {
      storesList,
      isCourier,
      isEditCartBtnLoading,
      getPrice,
      onClose,
      cartItemsUnavailable,
      isOnlyGWPUnavailable,
      isSampleInCart,
      showStoreNameWithAddress,
    } = this.props;

    const storesListOptions = storesList && storesList.filter((storeItem): boolean => (
      storeItem.itemsAvailable === INVENTORY.ALL
    )).map((storeItem): IOptionItem => {
      const address = `${showStoreNameWithAddress ?  `, ${storeItem.address}` : ''}`;
      const subHeading = `${storeItem.subHeading ? ` ${storeItem.subHeading}` : ''}`;
      const label =  `${storeItem.name}${subHeading}${address}`;

      return {
        label: label,
        value: storeItem.doorId,
      };
    });

    const isSelectStoreDisabled = this.selectedOption !== EDIT_CART_OPTION.CHANGE_STORE;
    const showOtherStores = storesListOptions && !!storesListOptions.length;
    const postmatesOrBopis = isCourier ? sameDayDelivery : clickCollectTitle;

    return (
      <StyledResponsiveModal
        className="edit-bag"
        title="Test"
        isOpen
        onMaskClick={onClose}
        position={LayoutPosition.MIDDLE}
      >
        <span
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={this.onCloseKeyDown}
          className="close-edit-bag-container"
        >
          <StyledRemoveIcon className="close-edit-bag" />
        </span>
        <div className="edit-bag-header">
          <h2 className="edit-bag-title">{editBagFirstCopy}</h2>
          <p className="edit-bag-message">{editBagDescription}</p>
        </div>
        {isSampleInCart && !TranslationUtils.isTranslationItemEmpty(samplesInfoMessage) && (
            <WarningMessage message={samplesInfoMessage} />
        )}
        <EditCartInfoWrapper className="edit-bag-info">
          <EditCartItemsWrapper className="edit-bag-items">
            <div className="item-availability none-available">
              <span className="item-availability-text">{unavailableItems} {postmatesOrBopis}</span>
            </div>
            <ShoppingBagWrapper className="shopping-bag-wrapper">
              <ShoppingBag
                cartItems={cartItemsUnavailable}
                enableSplits={true}
                getPrice={getPrice}
                hideHeader
                itemNotAvailableForPickUp={!isCourier}
              />
            </ShoppingBagWrapper>
          </EditCartItemsWrapper>
        </EditCartInfoWrapper>
        <div>
          <p className="edit-bag-select-option">{selectOption}</p>
          {showOtherStores && (
            <>
              <RadioBox
                onSelect={(): void => this.onChangeEditOption(EDIT_CART_OPTION.CHANGE_STORE)}
                selected={this.selectedOption === EDIT_CART_OPTION.CHANGE_STORE}
                dataTestId="change-store"
                className="select-radio-bopis-edit-bag"
              >
                <span
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: selectOtherStore,
                  }}
                />
              </RadioBox>
              <WrapperStyledDropdown>
                <StyledDropdown
                  isDisabled={isSelectStoreDisabled}
                  options={storesListOptions}
                  selectedValue={this.storeId}
                  name="store-select"
                  dataTestId="edit-bag-store-select"
                  onChange={this.onSelectDifferentStore}
                  placeholder={selectStore}
                  withFloatingLabel
                />
              </WrapperStyledDropdown>
            </>
          )}
          <RadioBox
            onSelect={(): void => this.onChangeEditOption(EDIT_CART_OPTION.SHIP_ALL_ITEMS)}
            selected={this.selectedOption === EDIT_CART_OPTION.SHIP_ALL_ITEMS}
            dataTestId="ship-all-items"
            className="select-radio-bopis-edit-bag"
          >
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: selectAllItems,
              }}
            />
          </RadioBox>
          <RadioBox
            onSelect={(): void => this.onChangeEditOption(EDIT_CART_OPTION.REMOVE_ITEMS)}
            selected={this.selectedOption === EDIT_CART_OPTION.REMOVE_ITEMS}
            dataTestId="remove-items"
            className="select-radio-bopis-edit-bag"
          >
            <span
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: isOnlyGWPUnavailable
                  ? removeItemsAndContinueWithGift
                  : removeItemsAndContinue,
              }}
            />
          </RadioBox>
        </div>
        <EditCartControlsWrapper className="edit-bag-controls">
          <CancelButton className="edit-bag-cancel" onClick={onClose}>{cancel}</CancelButton>
          <SubmitButton
            className="edit-bag-submit"
            onClick={this.onSubmit}
            isLoading={isEditCartBtnLoading}
          >
            {continueLabel}
          </SubmitButton>
        </EditCartControlsWrapper>
      </StyledResponsiveModal>
    );
  }
}

export default withTranslations(EditCart);
